/**
 * Модель страницы «План работ».
 *
 * Ключевая идея: строка сотрудника — это не набор независимых дней, а ОЧЕРЕДЬ назначений.
 * Позиция блока не хранится, а вычисляется из того, что стоит перед ним. Поэтому
 * «пустой день» физически не может образоваться при переносе — это свойство структуры,
 * а не правило, которое надо стеречь.
 *
 * Колонки — только рабочие дни (выходные и праздники не показываются вовсе).
 * Часть колонок «закреплена» (pinned) и в очереди не участвует:
 *   - прошедшие дни (их нельзя менять);
 *   - дни отсутствия (отпуск, болезнь и т.п.) — они привязаны к своей дате,
 *     очередь работ обтекает их так же, как обтекает выходные.
 */

/** Свободный день */
export interface FreeCell {
    kind: "free";
}

/** День работы по проекту */
export interface WorkCell {
    kind: "work";
    orderId: string;
}

/** День отсутствия — привязан к дате, очередью не двигается */
export interface AbsenceCell {
    kind: "absence";
    type: string;
    shortName: string;
}

export type PlanCell = FreeCell | WorkCell | AbsenceCell;

/** Отсутствие, которое ставят из плана: тип часов и его короткое имя для ячейки */
export interface AbsenceValue {
    type: string;
    shortName: string;
}

export const FREE_CELL: FreeCell = { kind: "free" };

/** Колонка = один рабочий день диапазона */
export interface PlanColumn {
    /** "2026-08-03" */
    day: string;
    /** "03" */
    dayOfMonth: string;
    month: number;
    year: number;
    /** Индекс месяца в диапазоне (0 — первый, 1 — второй) */
    periodIndex: number;
    isPast: boolean;
    isToday: boolean;
    /** Колонка из буферных месяцев — в модели есть, на экране нет */
    hidden: boolean;
}

/** Месяц диапазона — для шапки */
export interface PlanPeriod {
    month: number;
    year: number;
    /** "Август" */
    name: string;
    /** Рабочих дней в месяце */
    workDaysCount: number;
    /** Норма часов месяца */
    workHours: number;
    /** Индекс первой и последней колонки месяца */
    firstColumn: number;
    lastColumn: number;
}

export interface PlanRow {
    employerId: string;
    label: string;
    position: string;
    department: string;
    cells: PlanCell[];
}

/** Проект, доступный для назначения */
export interface PlanProject {
    id: string;
    inventoryNumber: number;
    /** "0123" */
    fullInventoryNumber: string;
    name: string;
    color: string;
}

/**
 * Непрерывный отрезок дней одного проекта — то, что рисуется полосой.
 * Дни отсутствия собираются в такие же блоки: их точно так же двигают и тянут,
 * отличается только заливка и подпись (буква в каждом дне вместо номера проекта).
 */
export interface PlanBlock {
    /** Проект работы; у блока отсутствия пустой */
    orderId: string;
    /** Заполнено, если блок — отпуск, больничный или БС */
    absence?: AbsenceValue;
    /** Индекс первой колонки */
    start: number;
    /** Длина в рабочих днях */
    length: number;
    /** Отрезок целиком в прошлом — показывается, но не редактируется */
    isPast: boolean;
}

export interface PlanSnapshot {
    columns: PlanColumn[];
    periods: PlanPeriod[];
    rows: PlanRow[];
    projects: PlanProject[];
}

/** Прямоугольное выделение ячеек в одной строке */
export interface CellSelection {
    rowIndex: number;
    start: number;
    length: number;
}

export function isWork(cell: PlanCell | undefined): cell is WorkCell {
    return !!cell && cell.kind === "work";
}

export function isAbsence(cell: PlanCell | undefined): cell is AbsenceCell {
    return !!cell && cell.kind === "absence";
}

export function isFree(cell: PlanCell | undefined): cell is FreeCell {
    return !!cell && cell.kind === "free";
}
